import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GoogleLogo, Envelope, Lock, UserPlus, SignIn } from '@phosphor-icons/react';

function Login() {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const navigate = useNavigate();

  const validatePassword = () => {
    if (password.length < 6) {
      setPasswordError('비밀번호는 최소 6자 이상이어야 합니다.');
      return false;
    }
    if (isSignUp && password !== confirmPassword) {
      setPasswordError('비밀번호가 일치하지 않습니다.');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;
    try {
      if (isSignUp) {
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
      navigate('/teacher-dashboard');
    } catch (error) {
      console.error("Authentication Error:", error);
      let errorMessage = "로그인에 실패했습니다. 다시 시도해주세요.";
      if (error.code === 'auth/user-not-found') {
        errorMessage = "등록되지 않은 이메일입니다.";
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = "잘못된 비밀번호입니다.";
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = "이미 가입된 메일입니다.";
      }
      alert(errorMessage);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      navigate('/teacher-dashboard');
    } catch (error) {
      console.error("Google Sign In Error:", error);
      alert(`Google 로그인 오류: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50/50 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            {isSignUp ? '계정 만들기' : '로그인'}
          </CardTitle>
          <CardDescription className="text-center">
            {isSignUp ? '이메일로 계정을 생성하세요' : '이메일과 비밀번호를 입력하세요'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">이메일</Label>
              <div className="relative">
                <Envelope className="absolute left-3 top-3 text-gray-400" size={18} />
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  className="pl-10"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">비밀번호</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
                <Input
                  id="password"
                  type="password"
                  placeholder="******"
                  className="pl-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">비밀번호 확인</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="******"
                    className="pl-10"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}
            {passwordError && <p className="text-red-500 text-sm">{passwordError}</p>}
            <Button type="submit" className="w-full">
              {isSignUp ? (
                <>
                  <UserPlus className="mr-2" size={18} /> 가입하기
                </>
              ) : (
                <>
                  <SignIn className="mr-2" size={18} /> 로그인
                </>
              )}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <Button variant="outline" type="button" onClick={handleGoogleSignIn} className="w-full">
            <GoogleLogo className="mr-2" size={18} weight="bold" />
            Google로 로그인
          </Button>
        </CardContent>
        <CardFooter>
          <Button
            variant="link"
            className="w-full"
            onClick={() => {
              setIsSignUp(!isSignUp);
              setPasswordError('');
              setConfirmPassword('');
            }}
          >
            {isSignUp ? '이미 계정이 있으신가요? 로그인' : '계정이 없으신가요? 가입하기'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

export default Login;